import React, { useState, useEffect, useMemo } from 'react';
import { fetchTradingDataFromSheets } from './servicesheet'; 
import TradeHistoryTab from './historytab';

import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  PieChart, Pie, Cell, Radar, RadarChart, PolarGrid, 
  PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import {
  Calendar, DollarSign, TrendingUp, TrendingDown, Activity,
  BarChart2, Clock, BookOpen, 
  Layers, Package, AlertTriangle, Award, EyeOff, Eye, 
  Search, RefreshCw, Zap, Target, Hexagon,
  User, Hash, Server, Briefcase, Percent, Shield, Pocket,
  UserCircle, MessageSquare, Edit3, Save, XCircle, ThumbsUp, AlertCircle,
  ClipboardList, Lightbulb, CheckCircle, XOctagon, Brain, ChevronsDown,
} from 'lucide-react';


const SCRIPT_URL = process.env.REACT_APP_SCRIPT_URL || "YOUR_APPS_SCRIPT_WEB_APP_URL"; 
const SHEET_PASSWORD = process.env.REACT_APP_SHEET_PASSWORD || ""; 

const TRADE_HISTORY_ITEMS_PER_PAGE = 10;
const INITIAL_JOURNAL_DISPLAY_COUNT = 5;
const JOURNAL_LOAD_MORE_COUNT = 5;
const NOTE_PREVIEW_LINES = 3;
const NOTE_PREVIEW_MAX_LENGTH = 120;

const COLORS_PALETTE = {
    primary: '#6e56cf', primaryLight: '#9e8cfc', primaryDark: '#4e3599',
    secondary: '#10b981', tertiary: '#f97316', quaternary: '#0ea5e9',
    buy: '#10b981', sell: '#f43f5e',
    success: '#10b981', warning: '#f97316', danger: '#f43f5e', info: '#0ea5e9',
    bgGradient1: '#0f172a', bgGradient2: '#1e293b',
    cardGradient1: 'rgba(30, 41, 59, 0.8)', cardGradient2: 'rgba(15, 23, 42, 0.9)',
    chart: ['#6e56cf', '#10b981', '#f97316', '#0ea5e9', '#f43f5e', '#a855f7', '#ec4899', '#f59e0b'],
};

// --- Institutional Metrics Calculations ---
const calculateInstitutionalMetrics = (dailyReturns, tradeHistory, accountInfo, statisticsFromSheet = {}) => {
  // const riskFreeRate = 0.02; // Annual risk-free rate, can be adjusted (Unused)

  const defaultMetrics = {
    maxDrawdown: "0.00", maxDrawdownDuration: 0, sharpeRatio: "N/A", sortinoRatio: "N/A",
    calmarRatio: "N/A", recoveryFactor: "N/A", profitToMaxDrawdown: "N/A", avgWinLossRatio: "0.00",
    consecutiveWins: 0, consecutiveLosses: 0, kellyPercentage: "N/A", ulcerIndex: "0.00",
    omega: "N/A", winRate: "0.00", totalNetProfit: "0.00"
  };

  if (!tradeHistory || tradeHistory.length === 0) {
    return defaultMetrics;
  }

  const sortedTrades = [...tradeHistory].sort((a, b) => {
    const dateA = new Date(`${String(a.date || '').replace(/\./g, '-')}T${a.time || '00:00:00'}`);
    const dateB = new Date(`${String(b.date || '').replace(/\./g, '-')}T${b.time || '00:00:00'}`);
    return (dateA.getTime() || 0) - (dateB.getTime() || 0);
  });

  // --- P/L Curve and Drawdown ---
  let peakEquityPL = 0;
  let clientCalculatedMaxDrawdownFraction = 0;
  const drawdownsForUlcer = [];

  const pnlCurve = [0];
  sortedTrades.forEach(trade => pnlCurve.push(pnlCurve[pnlCurve.length - 1] + (trade.netProfit || 0)));
  
  pnlCurve.forEach(cumulativePL => {
    if (cumulativePL > peakEquityPL) {
      peakEquityPL = cumulativePL;
    }
    const currentDrawdown = peakEquityPL > 0 ? Math.max(0, (peakEquityPL - cumulativePL) / peakEquityPL) : 0;
    drawdownsForUlcer.push(currentDrawdown);
    if (currentDrawdown > clientCalculatedMaxDrawdownFraction) {
      clientCalculatedMaxDrawdownFraction = currentDrawdown;
    }
  });

  // Use the more accurate, all-time drawdown from the sheet if available
  const finalFractionalMaxDrawdown = (statisticsFromSheet?.maxDrawdown / 100.0) || clientCalculatedMaxDrawdownFraction;
  const finalMaxDrawdownPercentage = finalFractionalMaxDrawdown * 100;
  const totalNetProfitFromTrades = pnlCurve[pnlCurve.length - 1];

  // --- Sharpe, Sortino, Omega (Based on daily returns) ---
  let sharpeRatioString = "N/A";
  let sortinoRatioString = "N/A";
  let omegaString = "N/A";
  const returns = dailyReturns || [];

  if (returns.length > 1) {
    const avgDailyReturn = returns.reduce((a, b) => a + b, 0) / returns.length;
    const stdDevDailyReturn = Math.sqrt(returns.reduce((sum, r) => sum + Math.pow(r - avgDailyReturn, 2), 0) / (returns.length - 1));

    if (stdDevDailyReturn > 0) {
      // Simple Sharpe (risk-free rate assumed 0 for daily returns)
      const annualizedSharpe = (avgDailyReturn / stdDevDailyReturn) * Math.sqrt(252);
      sharpeRatioString = annualizedSharpe.toFixed(2);
    } else {
      sharpeRatioString = avgDailyReturn > 0 ? "Infinity" : "0.00";
    }

    const targetReturn = 0;
    const downsideReturns = returns.filter(r => r < targetReturn);
    if (downsideReturns.length > 1) {
      const downsideDeviation = Math.sqrt(downsideReturns.reduce((sum, r) => sum + Math.pow(r - targetReturn, 2), 0) / downsideReturns.length);
      if (downsideDeviation > 0) {
        const annualizedSortino = (avgDailyReturn / downsideDeviation) * Math.sqrt(252);
        sortinoRatioString = annualizedSortino.toFixed(2);
      } else {
        sortinoRatioString = avgDailyReturn > 0 ? "Infinity" : "0.00";
      }
    } else if (avgDailyReturn > 0 && downsideReturns.length === 0){
        sortinoRatioString = "Infinity"; // Profitable with no downside days
    }

    const gains = returns.filter(r => r > targetReturn).reduce((sum, r) => sum + r, 0);
    const losses = Math.abs(returns.filter(r => r < targetReturn).reduce((sum, r) => sum + r, 0));
    if (losses > 0) {
      omegaString = (gains / losses).toFixed(2);
    } else if (gains > 0) {
      omegaString = "Infinity";
    } else {
      omegaString = "0.00";
    }
  }

  // --- Calmar Ratio ---
  let calmarRatioString = "N/A";
  const firstDate = new Date(`${String(sortedTrades[0].date).replace(/\./g, '-')}T${sortedTrades[0].time || '00:00:00'}`);
  const lastDate = new Date(`${String(sortedTrades[sortedTrades.length - 1].date).replace(/\./g, '-')}T${sortedTrades[sortedTrades.length - 1].time || '00:00:00'}`);
  if (!isNaN(firstDate.getTime()) && !isNaN(lastDate.getTime())) {
    const tradingPeriodDays = Math.max(1, (lastDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24));
    if (tradingPeriodDays > 90) { // Standard practice: only for periods > ~3 months
      const tradingPeriodYears = tradingPeriodDays / 365.25;
      const initial = accountInfo.initialBalance > 0 ? accountInfo.initialBalance : (accountInfo.balance - totalNetProfitFromTrades);
      if (initial > 0) {
        const ending = initial + totalNetProfitFromTrades;
        const cagr = Math.pow(ending / initial, 1 / tradingPeriodYears) - 1;
        if (finalFractionalMaxDrawdown > 0) {
          calmarRatioString = (cagr / finalFractionalMaxDrawdown).toFixed(2);
        }
      }
    }
  }
  
  const recoveryFactorValue = statisticsFromSheet?.recoveryFactor || 0;
  
  const ulcerIndex = drawdownsForUlcer.length > 0 ? Math.sqrt(drawdownsForUlcer.reduce((sum, dd) => sum + Math.pow(dd, 2), 0) / drawdownsForUlcer.length) * 100 : 0;
  
  const winningTrades = tradeHistory.filter(t => (t.netProfit || 0) > 0);
  const losingTrades = tradeHistory.filter(t => (t.netProfit || 0) < 0);
  const winRate = tradeHistory.length > 0 ? winningTrades.length / tradeHistory.length : 0;
  const avgWin = winningTrades.length > 0 ? winningTrades.reduce((sum, t) => sum + t.netProfit, 0) / winningTrades.length : 0;
  const avgLoss = losingTrades.length > 0 ? Math.abs(losingTrades.reduce((sum, t) => sum + t.netProfit, 0) / losingTrades.length) : 0;
  const avgWinLossRatio = avgLoss > 0 ? avgWin / avgLoss : 0;

  let kellyPercentageString = "0.00%";
  if (losingTrades.length === 0 && winningTrades.length > 0) {
    kellyPercentageString = "100.00%";
  } else if (avgWinLossRatio > 0) {
    const kellyFraction = winRate - ((1 - winRate) / avgWinLossRatio);
    kellyPercentageString = (Math.max(0, kellyFraction) * 100).toFixed(2) + "%";
  }

  let currentConsecutiveWins = 0, maxConsecutiveWins = 0;
  let currentConsecutiveLosses = 0, maxConsecutiveLosses = 0;
  sortedTrades.forEach(trade => {
    if ((trade.netProfit || 0) > 0) { currentConsecutiveWins++; currentConsecutiveLosses = 0; maxConsecutiveWins = Math.max(maxConsecutiveWins, currentConsecutiveWins); } 
    else if ((trade.netProfit || 0) < 0) { currentConsecutiveLosses++; currentConsecutiveWins = 0; maxConsecutiveLosses = Math.max(maxConsecutiveLosses, currentConsecutiveLosses); }
  });
  
  return {
    maxDrawdown: finalMaxDrawdownPercentage.toFixed(2),
    sharpeRatio: sharpeRatioString,
    sortinoRatio: sortinoRatioString,
    calmarRatio: calmarRatioString,
    recoveryFactor: isFinite(recoveryFactorValue) ? recoveryFactorValue.toFixed(2) : "N/A",
    avgWinLossRatio: avgWinLossRatio.toFixed(2),
    consecutiveWins: maxConsecutiveWins,
    kellyPercentage: kellyPercentageString,
    ulcerIndex: ulcerIndex.toFixed(2),
    omega: omegaString,
    winRate: (winRate * 100).toFixed(2),
    totalNetProfit: totalNetProfitFromTrades.toFixed(2),
  };
};

// --- UI Components ---
const Card = ({ children, className = '' }) => (
  <div className={`relative bg-gradient-to-br from-gray-800/90 to-gray-900/95 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-xl overflow-hidden ${className}`}>
    <div className="absolute inset-0 bg-gradient-to-tr from-primary-700/5 to-primary-500/10 pointer-events-none"></div>
    {children}
  </div>
);

const ChartCard = ({ title, icon, children, className = '', chartHeight = 'h-96' }) => (
  <Card className={className}>
    <div className="p-5">
      <h3 className="flex items-center text-lg font-semibold text-gray-100 mb-4">
        {icon && React.cloneElement(icon, { className: "h-5 w-5 mr-2 text-primary-400" })}
        {title}
      </h3>
      <div className={chartHeight}>
        {children}
      </div>
    </div>
  </Card>
);

const StatsCard = ({ title, value, icon, trend, trendValue, className = '', iconBgColor = 'bg-primary-600', valueClassName = 'text-xl', subtitle }) => (
  <Card className={className}>
    <div className="p-5">
      <div className="flex items-center">
        <div className={`p-3 rounded-xl ${iconBgColor} bg-opacity-20 text-white shadow-lg flex items-center justify-center`}>
          {icon}
        </div>
        <div className="ml-4 flex-grow">
          <p className="text-sm font-medium text-gray-400">{title}</p>
          <h3 className={`${valueClassName} font-bold text-gray-100`}>{value}</h3>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
          {trend && (
            <div className={`flex items-center mt-1 text-xs font-medium ${trend === 'up' ? 'text-green-400' : 'text-red-400'}`}>
              {trend === 'up' ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
              {trendValue}
            </div>
          )}
        </div>
      </div>
    </div>
  </Card>
);

const TabButton = ({ active, onClick, children }) => (
  <button 
    className={`whitespace-nowrap py-4 px-4 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center ${
      active 
        ? 'border-primary-500 text-primary-400 font-semibold' 
        : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-600'
    }`} 
    onClick={onClick}
  >
    {children}
  </button>
);

const Button = ({ children, variant = 'primary', icon, className = '', disabled, ...props }) => {
  const variantClasses = { 
    primary: 'bg-primary-600 hover:bg-primary-700 focus:ring-primary-500', 
    secondary: 'bg-gray-700 hover:bg-gray-800 focus:ring-gray-500', 
    outline: 'bg-transparent border border-gray-600 hover:bg-gray-800 focus:ring-gray-500',
  };
  return (
    <button 
      className={`inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all duration-200 shadow-lg ${variantClasses[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`} 
      disabled={disabled} 
      {...props}
    >
      {icon && React.cloneElement(icon, { className: `h-4 w-4 ${children ? 'mr-2' : ''}` })}
      {children}
    </button>
  );
};

const SearchInput = ({ value, onChange, placeholder = 'Search...', className = '' }) => (
  <div className={`relative w-full md:w-auto ${className}`}>
    <input 
      type="text" 
      placeholder={placeholder} 
      value={value} 
      onChange={onChange} 
      className="w-full md:w-72 pl-10 pr-4 py-2 rounded-lg bg-gray-800/60 border border-gray-700 text-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder-gray-500 text-sm transition-all" 
    />
    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
  </div>
);

// --- MODAL COMPONENT for Editing Notes ---
const EditNoteModal = ({ isOpen, onClose, trade, onSave }) => {
  const [details, setDetails] = useState({
    notes: "", strategy: "", reasonEntry: "", reasonExit: "", lessonsLearned: ""
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen && trade) {
      setDetails({
        notes: trade.notes || "",
        strategy: trade.strategy || "",
        reasonEntry: trade.reasonEntry || "",
        reasonExit: trade.reasonExit || "",
        lessonsLearned: trade.lessonsLearned || ""
      });
      setError("");
    }
  }, [isOpen, trade]);

  if (!isOpen || !trade) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDetails(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError("");
    try {
      await onSave(trade.ticket || trade.id, details); 
      setIsSaving(false);
      onClose(); 
    } catch (err) {
      console.error("Error saving note in modal:", err);
      setError(err.message || "Failed to save journal details. Please try again.");
      setIsSaving(false);
    }
  };

  const inputFields = [
    { name: "strategy", label: "Strategy/Setup", placeholder: "e.g., EMA Bounce, News Fade", icon: <ClipboardList size={16}/>, rows:2 },
    { name: "reasonEntry", label: "Reason for Entry", placeholder: "Specific chart patterns, indicators, confluences...", icon: <CheckCircle size={16}/>, rows:3 },
    { name: "reasonExit", label: "Reason for Exit", placeholder: "TP hit, SL hit, discretionary, time-based...", icon: <XOctagon size={16}/>, rows:3 },
    { name: "lessonsLearned", label: "Lessons Learned", placeholder: "What went well? What to improve? Key takeaways.", icon: <Lightbulb size={16}/>, rows:3 },
    { name: "notes", label: "General Notes/Original Comment", placeholder: "Any other observations or original MT5 comment...", icon: <MessageSquare size={16}/>, rows:3 },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm flex items-center justify-center z-[100] p-4 transition-opacity duration-300 ease-in-out" style={{opacity: isOpen ? 1 : 0}}>
      <Card className="w-full max-w-2xl shadow-2xl transform transition-all duration-300 ease-in-out" style={{opacity: isOpen ? 1 : 0, transform: isOpen ? 'translateY(0)' : 'translateY(-20px)'}}>
        <div className="p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4 sticky top-0 bg-gray-800/80 backdrop-blur-sm py-2 -mx-6 px-6 z-10 border-b border-gray-700/50">
            <h3 className="text-xl font-semibold text-gray-100 flex items-center">
              <Edit3 size={20} className="mr-2 text-primary-400" />
              Journal for Trade #{trade.ticket || trade.id}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-200 transition-colors" disabled={isSaving}>
              <XCircle size={24} />
            </button>
          </div>
          <p className="text-sm text-gray-400 mb-3">
            Symbol: <span className="font-semibold text-gray-200">{trade.symbol}</span> | 
            Type: <span className="font-semibold text-gray-200">{trade.type}</span> | 
            Profit: <span className={`font-semibold ${trade.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {trade.profit?.toFixed(2)}
            </span>
          </p>
          
          <div className="space-y-4">
            {inputFields.map(field => (
              <div key={field.name}>
                <label htmlFor={field.name} className="block text-sm font-medium text-primary-300 mb-1 flex items-center">
                  {React.cloneElement(field.icon, {className: "mr-2"})} {field.label}
                </label>
                <textarea
                  id={field.name}
                  name={field.name}
                  value={details[field.name]}
                  onChange={handleChange}
                  placeholder={field.placeholder}
                  rows={field.rows}
                  className="w-full p-3 rounded-lg bg-gray-700/50 border border-gray-600 text-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder-gray-500 text-sm resize-y"
                  disabled={isSaving}
                />
              </div>
            ))}
          </div>

          {error && <p className="text-xs text-red-400 mt-3 flex items-center"><AlertCircle size={14} className="mr-1"/> {error}</p>}
          <div className="mt-6 flex justify-end space-x-3 sticky bottom-0 bg-gray-800/80 backdrop-blur-sm py-3 -mx-6 px-6 border-t border-gray-700/50">
            <Button variant="outline" onClick={onClose} disabled={isSaving}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} disabled={isSaving} icon={isSaving ? <RefreshCw className="animate-spin" /> : <Save />}>
              {isSaving ? "Saving..." : "Save Journal"}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

// --- Tab Specific Components ---
const OverviewTab = ({ accountInfo, statistics, cumulativeData, institutionalMetrics }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 xl:gap-6">
      <StatsCard title="Balance" value={`${accountInfo.currency} ${accountInfo.balance?.toFixed(2) || '0.00'}`} icon={<DollarSign className="h-6 w-6" />} iconBgColor="bg-blue-600" />
      <StatsCard title="Equity" value={`${accountInfo.currency} ${accountInfo.equity?.toFixed(2) || '0.00'}`} icon={<Zap className="h-6 w-6" />} iconBgColor="bg-teal-600" />
      <StatsCard title="Current Profit" value={`${accountInfo.currency} ${accountInfo.profit?.toFixed(2) || '0.00'}`} icon={<TrendingUp className="h-6 w-6" />} iconBgColor="bg-green-600" />
      <StatsCard title="Margin" value={`${accountInfo.currency} ${accountInfo.margin?.toFixed(2) || '0.00'}`} icon={<Shield className="h-6 w-6" />} iconBgColor="bg-red-600" />
      <StatsCard title="Free Margin" value={`${accountInfo.currency} ${accountInfo.freeMargin?.toFixed(2) || '0.00'}`} icon={<Pocket className="h-6 w-6" />} iconBgColor="bg-sky-600" />
      <StatsCard title="Margin Level" value={`${accountInfo.marginLevel?.toFixed(2) || '0.00'}%`} icon={<Percent className="h-6 w-6" />} iconBgColor="bg-indigo-600" />
      <StatsCard title="Total Trades" value={statistics.totalTrades || 0} icon={<Activity className="h-6 w-6" />} iconBgColor="bg-purple-600" />
      <StatsCard title="Win Rate" value={`${institutionalMetrics.winRate || '0.00'}%`} icon={<Award className="h-6 w-6" />} iconBgColor="bg-cyan-600" />
    </div>
    
    <Card className="p-0">
      <div className="p-5">
        <h3 className="flex items-center text-lg font-semibold text-gray-100 mb-4">
          <UserCircle className="h-5 w-5 mr-2 text-primary-400" />Account Details
        </h3>
      </div>
      <div className="border-t border-gray-700/50">
        <dl className="divide-y divide-gray-700/50">
          {[
            { label: "Account Name", value: "HON-SONAR", icon: <User /> }, 
            { label: "Account Number", value: "110786194", icon: <Hash /> },
            { label: "Server", value: accountInfo.server, icon: <Server /> }, 
            { label: "Company", value: accountInfo.company, icon: <Briefcase /> },
            { label: "Leverage", value: accountInfo.leverage ? `1:${accountInfo.leverage}` : 'N/A', icon: <Layers /> }
          ].map((item, index) => (
            <div key={index} className="px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 items-center hover:bg-gray-800/30 transition-colors duration-150">
              <dt className="text-sm font-medium text-gray-400 flex items-center">
                {item.icon && React.cloneElement(item.icon, { className: "h-4 w-4 mr-2 text-primary-400" })}
                {item.label}
              </dt>
              <dd className="mt-1 text-sm text-gray-200 sm:mt-0 sm:col-span-2">{item.value || 'N/A'}</dd>
            </div>
          ))}
        </dl>
      </div>
    </Card>
    
    <ChartCard title="Equity Curve with Drawdown" icon={<Activity />} className="p-0">
      <div className="px-5 pb-5">
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={cumulativeData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS_PALETTE.primary} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={COLORS_PALETTE.primary} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorDrawdown" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS_PALETTE.danger} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={COLORS_PALETTE.danger} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.5} />
              <XAxis dataKey="date" stroke="#9ca3af" tick={{fill: '#9ca3af', fontSize: 12}} />
              <YAxis yAxisId="equity" stroke="#9ca3af" tick={{fill: '#9ca3af', fontSize: 12}} domain={['auto', 'auto']} />
              <YAxis yAxisId="drawdown" orientation="right" stroke="#ef4444" tick={{fill: '#ef4444', fontSize: 12}} domain={[0, 'auto']} />
              <Tooltip 
                contentStyle={{ backgroundColor: 'rgba(17, 24, 39, 0.8)', backdropFilter: 'blur(4px)', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '0.5rem', color: '#e5e7eb' }} 
                labelStyle={{ color: '#9ca3af', fontWeight: 'bold' }} 
                formatter={(value, name) => {
                  if (name === 'Drawdown %') return [`${value?.toFixed(2)}%`, name];
                  return [`${accountInfo.currency} ${value?.toFixed(2) || '0.00'}`, name];
                }} 
              />
              <ReferenceLine yAxisId="equity" y={0} stroke="#ef4444" strokeDasharray="3 3" />
              <Area yAxisId="equity" type="monotone" dataKey="cumulativeProfit" stroke={COLORS_PALETTE.primary} strokeWidth={2} fill="url(#colorProfit)" activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }} name="Equity" />
              <Area yAxisId="drawdown" type="monotone" dataKey="drawdownPercent" stroke={COLORS_PALETTE.danger} strokeWidth={1} fill="url(#colorDrawdown)" name="Drawdown %" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      
    </ChartCard>
    
    <ChartCard title="Institutional Performance Metrics" icon={<Target />}>
    <div className="max-h-[400px] overflow-y-auto md:overflow-visible">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 text-sm">
        <div className="rounded-xl bg-gray-800/50 p-4 border border-gray-700/50">
          <div className="text-gray-400 mb-1 text-xs">Max Drawdown</div>
          <div className="text-lg font-semibold text-red-400">{institutionalMetrics.maxDrawdown}%</div>
          <div className="mt-1 text-xs text-gray-500">Peak to Valley</div>
        </div>
        <div className="rounded-xl bg-gray-800/50 p-4 border border-gray-700/50">
          <div className="text-gray-400 mb-1 text-xs">Sharpe Ratio</div>
          <div className="text-lg font-semibold text-white">{institutionalMetrics.sharpeRatio}</div>
          <div className="mt-1 text-xs text-gray-500">Risk-Adjusted Return</div>
        </div>
        <div className="rounded-xl bg-gray-800/50 p-4 border border-gray-700/50">
          <div className="text-gray-400 mb-1 text-xs">Sortino Ratio</div>
          <div className="text-lg font-semibold text-white">{institutionalMetrics.sortinoRatio}</div>
          <div className="mt-1 text-xs text-gray-500">Downside Risk</div>
        </div>
        <div className="rounded-xl bg-gray-800/50 p-4 border border-gray-700/50">
          <div className="text-gray-400 mb-1 text-xs">Calmar Ratio</div>
          <div className="text-lg font-semibold text-white">{institutionalMetrics.calmarRatio}</div>
          <div className="mt-1 text-xs text-gray-500">Return/Max DD</div>
        </div>
        <div className="rounded-xl bg-gray-800/50 p-4 border border-gray-700/50">
          <div className="text-gray-400 mb-1 text-xs">Recovery Factor</div>
          <div className="text-lg font-semibold text-white">{institutionalMetrics.recoveryFactor}</div>
          <div className="mt-1 text-xs text-gray-500">Profit/Max DD</div>
        </div>
        <div className="rounded-xl bg-gray-800/50 p-4 border border-gray-700/50">
          <div className="text-gray-400 mb-1 text-xs">Win/Loss Ratio</div>
          <div className="text-lg font-semibold text-white">{institutionalMetrics.avgWinLossRatio}</div>
          <div className="mt-1 text-xs text-gray-500">Avg Win/Avg Loss</div>
        </div>
        <div className="rounded-xl bg-gray-800/50 p-4 border border-gray-700/50">
          <div className="text-gray-400 mb-1 text-xs">Kelly %</div>
          <div className="text-lg font-semibold text-white">{institutionalMetrics.kellyPercentage}</div>
          <div className="mt-1 text-xs text-gray-500">Optimal Position Size</div>
        </div>
        <div className="rounded-xl bg-gray-800/50 p-4 border border-gray-700/50">
          <div className="text-gray-400 mb-1 text-xs">Ulcer Index</div>
          <div className="text-lg font-semibold text-white">{institutionalMetrics.ulcerIndex}</div>
          <div className="mt-1 text-xs text-gray-500">Downside Volatility</div>
        </div>
        <div className="rounded-xl bg-gray-800/50 p-4 border border-gray-700/50">
          <div className="text-gray-400 mb-1 text-xs">Omega Ratio</div>
          <div className="text-lg font-semibold text-white">{institutionalMetrics.omega}</div>
          <div className="mt-1 text-xs text-gray-500">Gains vs Losses</div>
        </div>
        <div className="rounded-xl bg-gray-800/50 p-4 border border-gray-700/50">
          <div className="text-gray-400 mb-1 text-xs">Consecutive Wins</div>
          <div className="text-lg font-semibold text-green-400">{institutionalMetrics.consecutiveWins}</div>
          <div className="mt-1 text-xs text-gray-500">Max Streak</div>
        </div>
      </div>
      </div>
    </ChartCard>
  </div>
);

const PerformanceTab = ({ data }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <ChartCard title="Symbol Performance (Profit/Loss)" icon={<BarChart2 />} className="p-0">
        <div className="px-5 pb-5 h-96">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.symbolPerformance} layout="vertical" margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis type="number" stroke="#9ca3af" tick={{fill: '#9ca3af', fontSize: 12}} />
              <YAxis type="category" dataKey="symbol" stroke="#9ca3af" tick={{fill: '#d1d5db', fontSize: 12}} width={80} />
              <Tooltip contentStyle={{ backgroundColor: 'rgba(17, 24, 39, 0.8)', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '0.5rem', color: '#e5e7eb' }} formatter={(value) => `${data.accountInfo.currency} ${value?.toFixed(2)}`} />
              <Legend wrapperStyle={{ color: '#e5e7eb' }} />
              <Bar dataKey="profit" name="Profit/Loss" radius={[0, 4, 4, 0]}>
                {data.symbolPerformance.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.profit >= 0 ? COLORS_PALETTE.success : COLORS_PALETTE.danger} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>
      
      <ChartCard title="Time Performance (Profit by Hour)" icon={<Clock />} className="p-0">
        <div className="px-5 pb-5 h-96">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.timePerformance} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="hour" stroke="#9ca3af" tick={{fill: '#9ca3af', fontSize: 12}} />
              <YAxis yAxisId="leftProf" stroke="#9ca3af" tick={{fill: '#9ca3af', fontSize: 12}} />
              <YAxis yAxisId="rightWR" orientation="right" stroke={COLORS_PALETTE.secondary} tick={{fill:COLORS_PALETTE.secondary, fontSize:12}} domain={[0,100]} />
              <Tooltip 
                contentStyle={{ backgroundColor: 'rgba(17, 24, 39, 0.8)', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '0.5rem', color: '#e5e7eb' }} 
                formatter={(value, name) => name === "Win Rate (%)" ? `${value?.toFixed(1)}%` : `${data.accountInfo.currency} ${value?.toFixed(2)}`} 
              />
              <Legend wrapperStyle={{ color: '#e5e7eb' }} />
              <Line yAxisId="leftProf" type="monotone" dataKey="profit" name="Profit" stroke={COLORS_PALETTE.primary} strokeWidth={2} activeDot={{ r: 8 }} />
              <Line yAxisId="rightWR" type="monotone" dataKey="winRate" name="Win Rate (%)" stroke={COLORS_PALETTE.secondary} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>
    </div>
    
    <ChartCard title="Performance Metrics Radar" icon={<Target />} className="p-0">
      <div className="px-5 pb-5 h-96">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart cx="50%" cy="50%" outerRadius="80%" data={[
            { subject: 'Win Rate', A: data.statistics.winRate || 0, fullMark: 100 },
            { subject: 'Profit Factor', A: (data.statistics.profitFactor || 0) * 20, fullMark: 100 },
            { subject: 'Expectancy', A: (data.statistics.expectancy || 0) * 50, fullMark: 100 },
            { subject: 'Recovery', A: 100 - (parseFloat(data.institutionalMetrics?.maxDrawdown) || 0), fullMark: 100 },
          ]}>
            <PolarGrid stroke="#374151" />
            <PolarAngleAxis dataKey="subject" tick={{ fill: '#d1d5db', fontSize: 12 }} />
            <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: '#9ca3af', fontSize: 10 }} />
            <Radar name="Performance" dataKey="A" stroke={COLORS_PALETTE.primary} fill={COLORS_PALETTE.primary} fillOpacity={0.2} />
            <Tooltip contentStyle={{ backgroundColor: 'rgba(17, 24, 39, 0.8)', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '0.5rem', color: '#e5e7eb' }} />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  </div>
);

// --- Symbols Tab ---
const SymbolsTab = ({ symbolPerformance, accountInfo }) => {
  const profitDistributionData = useMemo(() => {
    return symbolPerformance
      .filter(s => s.profit !== 0) 
      .map(s => ({ name: s.symbol, value: Math.abs(s.profit) })); 
  }, [symbolPerformance]);

  return (
    <div className="space-y-6">
      <Card className="overflow-hidden">
        <div className="p-5 flex justify-between items-center border-b border-gray-700">
          <h3 className="flex items-center text-lg font-semibold text-gray-100">
            <Package className="h-5 w-5 mr-2 text-primary-400" />Symbol Performance Summary
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-800/50">
              <tr>
                {["Symbol", "Trades", "Win Rate (%)", "Total Profit", "Avg. Profit/Trade", "Avg. Duration"].map(header => (
                  <th key={header} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-transparent divide-y divide-gray-700">
              {symbolPerformance.map((s, i) => (
                <tr key={s.symbol} className="hover:bg-gray-800/30">
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-white flex items-center">
                    <div className="w-2 h-6 rounded-sm mr-3" style={{ backgroundColor: COLORS_PALETTE.chart[i % COLORS_PALETTE.chart.length] }}></div>
                    {s.symbol}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-300">{s.trades}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-300">{s.winRate?.toFixed(1)}%</td>
                  <td className={`px-6 py-4 whitespace-nowrap font-medium ${s.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {accountInfo.currency} {s.profit?.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-300">
                    {accountInfo.currency} {(s.profit / (s.trades || 1))?.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-300">{s.avgDuration}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Profit Contribution by Symbol" icon={<PieChart />} chartHeight="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie 
                data={profitDistributionData} 
                dataKey="value" 
                nameKey="name" 
                cx="50%" 
                cy="50%" 
                outerRadius={100} 
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {profitDistributionData.map((entry, index) => {
                  const originalSymbolData = symbolPerformance.find(s => s.symbol === entry.name);
                  return (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={originalSymbolData && originalSymbolData.profit >= 0 
                        ? COLORS_PALETTE.chart[index % COLORS_PALETTE.chart.length] 
                        : COLORS_PALETTE.danger
                      } 
                    />
                  );
                })}
              </Pie>
              <Tooltip 
                formatter={(value, name, props) => [
                  `${accountInfo.currency} ${symbolPerformance.find(s=>s.symbol === props.payload.name)?.profit.toFixed(2)}`, 
                  name
                ]} 
              />
              <Legend iconType="circle" />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Trade Volume by Symbol" icon={<BarChart2 />} chartHeight="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={symbolPerformance} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="symbol" stroke="#9ca3af" tick={{fill: '#9ca3af', fontSize: 12}} />
              <YAxis stroke="#9ca3af" tick={{fill: '#9ca3af', fontSize: 12}} />
              <Tooltip contentStyle={{ backgroundColor: 'rgba(17, 24, 39, 0.8)', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '0.5rem', color: '#e5e7eb' }} />
              <Legend />
              <Bar dataKey="trades" name="Number of Trades" radius={[4, 4, 0, 0]}>
                {symbolPerformance.map((entry, index) => (
                  <Cell key={`cell-vol-${index}`} fill={COLORS_PALETTE.chart[index % COLORS_PALETTE.chart.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
};

// --- Open Positions Tab ---
const OpenPositionsTab = ({ openPositions, accountInfo }) => {
  const profitBySymbolData = useMemo(() => {
    if (!openPositions) return [];
    const symbolMap = openPositions.reduce((acc, pos) => {
      acc[pos.symbol] = (acc[pos.symbol] || 0) + (pos.profit || 0);
      return acc;
    }, {});
    return Object.entries(symbolMap).map(([symbol, profit]) => ({ symbol, profit }));
  }, [openPositions]);

  const exposureBySymbolData = useMemo(() => {
    if(!openPositions) return [];
    const exposureMap = openPositions.reduce((acc, pos) => {
      acc[pos.symbol] = (acc[pos.symbol] || 0) + (pos.volume || 0);
      return acc;
    }, {});
    return Object.entries(exposureMap).map(([name, value]) => ({ name, value: parseFloat(value.toFixed(4))}));
  }, [openPositions]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Current P/L by Symbol (Open)" icon={<BarChart2 />} chartHeight="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={profitBySymbolData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="symbol" stroke="#9ca3af" tick={{fill: '#9ca3af', fontSize: 12}} />
              <YAxis stroke="#9ca3af" tick={{fill: '#9ca3af', fontSize: 12}} />
              <Tooltip formatter={(value) => `${accountInfo.currency} ${value?.toFixed(2)}`} contentStyle={{ backgroundColor: 'rgba(17, 24, 39, 0.8)', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '0.5rem', color: '#e5e7eb' }} />
              <Bar dataKey="profit" name="Current P/L" radius={[4,4,0,0]}>
                {profitBySymbolData.map((entry, index) => (
                  <Cell key={`cell-op-${index}`} fill={entry.profit >= 0 ? COLORS_PALETTE.success : COLORS_PALETTE.danger} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        
        <ChartCard title="Exposure by Symbol (Open Volume)" icon={<PieChart />} chartHeight="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie 
                data={exposureBySymbolData} 
                dataKey="value" 
                nameKey="name" 
                cx="50%" 
                cy="50%" 
                outerRadius={90} 
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {exposureBySymbolData.map((entry, index) => (
                  <Cell key={`cell-exp-${index}`} fill={COLORS_PALETTE.chart[(index + 2) % COLORS_PALETTE.chart.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `${value} lots`} />
              <Legend iconType="circle" />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <Card className="overflow-hidden">
        <div className="p-5 flex justify-between items-center border-b border-gray-700">
          <h3 className="flex items-center text-lg font-semibold text-gray-100">
            <Layers className="h-5 w-5 mr-2 text-primary-400" />Open Positions Table
          </h3>
          <Button variant="outline" icon={<RefreshCw />} className="text-sm" onClick={() => window.location.reload()}>
            Refresh
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-800/50">
              <tr>
                {["Ticket", "Time", "Symbol", "Type", "Volume", "Open Price", "S/L", "T/P", "Current Price", "Profit", "Swap", "Total", "Pips", "Duration", "Comment"].map(h => (
                  <th key={h} className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-transparent divide-y divide-gray-700/50">
              {openPositions && openPositions.map(p => (
                <tr key={p.ticket} className="hover:bg-gray-800/30">
                  <td className="px-5 py-4 text-xs text-gray-400 font-mono">{p.ticket}</td>
                  <td className="px-5 py-4 text-sm text-gray-300">{p.time}</td>
                  <td className="px-5 py-4 text-sm font-medium text-white">{p.symbol}</td>
                  <td className="px-5 py-4 text-sm">
                    <span className={`font-semibold ${p.type === 'Buy' ? 'text-buy' : 'text-sell'}`}>{p.type}</span>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-300">{p.volume}</td>
                  <td className="px-5 py-4 text-sm text-gray-300 font-mono">{p.price?.toFixed(5)}</td>
                  <td className="px-5 py-4 text-sm text-red-400 font-mono">{p.sl?.toFixed(5)}</td>
                  <td className="px-5 py-4 text-sm text-green-400 font-mono">{p.tp?.toFixed(5)}</td>
                  <td className="px-5 py-4 text-sm text-gray-300 font-mono">{p.currentPrice?.toFixed(5)}</td>
                  <td className={`px-5 py-4 text-sm font-medium ${p.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {accountInfo.currency} {p.profit?.toFixed(2)}
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-300">{p.swap?.toFixed(2)}</td>
                  <td className={`px-5 py-4 text-sm font-medium ${p.total >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {accountInfo.currency} {p.total?.toFixed(2)}
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-300">{p.pips?.toFixed(1)}</td>
                  <td className="px-5 py-4 text-sm text-gray-300">{p.duration}</td>
                  <td className="px-5 py-4 text-sm text-gray-400 max-w-xs truncate" title={p.comment}>{p.comment || '-'}</td>
                </tr>
              ))}
              {(!openPositions || openPositions.length === 0) && (
                <tr>
                  <td colSpan={15} className="px-5 py-10 text-center text-gray-500">No open positions.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

// --- Journal Tab & Card ---
const JournalEntryCard = ({ trade, onEdit, currency }) => {
  const [detailsVisible, setDetailsVisible] = useState(false); 
  const profitColor = trade.profit >= 0 ? 'text-green-400' : 'text-red-400';
  const tradeTypeColor = trade.type?.toLowerCase() === 'buy' ? 'text-buy' : 'text-sell';

  const journalFields = useMemo(() => [
    { name: "strategy", label: "Strategy/Setup", value: trade.strategy, icon: <ClipboardList size={15}/> },
    { name: "reasonEntry", label: "Reason for Entry", value: trade.reasonEntry, icon: <CheckCircle size={15}/> },
    { name: "reasonExit", label: "Reason for Exit", value: trade.reasonExit, icon: <XOctagon size={15}/> },
    { name: "lessonsLearned", label: "Lessons Learned", value: trade.lessonsLearned, icon: <Lightbulb size={15}/> },
    { name: "notes", label: "General Notes", value: trade.notes, icon: <MessageSquare size={15}/> } 
  ], [trade.strategy, trade.reasonEntry, trade.reasonExit, trade.lessonsLearned, trade.notes]);

  const hasAnyDetail = useMemo(() => 
    journalFields.some(field => field.value && String(field.value).trim() !== "")
  , [journalFields]);

  const truncateText = (text, maxLength = NOTE_PREVIEW_MAX_LENGTH, maxLines = NOTE_PREVIEW_LINES) => {
    if (!text || String(text).trim() === "") return "";
    const lines = String(text).split('\n');
    const relevantLines = lines.slice(0, maxLines);
    let preview = relevantLines.join('\n');
    
    if (lines.length > maxLines || preview.length > maxLength) {
      preview = preview.substring(0, maxLength);
      const lastSpace = preview.lastIndexOf(' ');
      if (lastSpace > 0 && preview.length - lastSpace < 20) { 
        preview = preview.substring(0, lastSpace);
      }
      preview += "...";
    }
    return preview;
  };

  const previewText = useMemo(() => {
    if (!hasAnyDetail) return "No journal details entered. Click 'Edit' to add.";
    
    const firstDetailFieldWithValue = journalFields.find(f => f.value && String(f.value).trim() !== "");
    if (firstDetailFieldWithValue) {
      return truncateText(firstDetailFieldWithValue.value);
    }
    return "Click 'Show Details' to view more.";
  }, [hasAnyDetail, journalFields]);

  return (
    <Card className="mb-6 hover:shadow-primary-500/30 transition-shadow duration-300">
      <div className="p-5">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 pb-3 border-b border-gray-700/50">
          <div className="mb-2 sm:mb-0">
            <span className="text-xs text-gray-400">{trade.date} {trade.time} (ID: {trade.ticket || trade.id})</span>
            <h4 className="text-lg font-semibold text-gray-100">
              <span className={`${tradeTypeColor} font-bold`}>{trade.type?.toUpperCase()}</span> {trade.symbol}
            </h4>
          </div>
          <div className="text-right">
            <p className={`text-xl font-bold ${profitColor}`}>
              {trade.profit >= 0 ? '+' : ''}{trade.profit?.toFixed(2) || '0.00'} {currency || ''}
            </p>
            <p className="text-xs text-gray-500">Vol: {trade.volume || 'N/A'} | Pips: {trade.pips?.toFixed(1) || 'N/A'}</p>
          </div>
        </div>
        
        <div className="mt-3">
          <div className="flex justify-between items-center mb-2">
            <h5 className="text-sm font-semibold text-primary-400 flex items-center">
              <Brain size={16} className="mr-2" /> Journal Details:
            </h5>
            <div className="flex items-center space-x-2">
              <Button variant="outline" icon={<Edit3 />} onClick={onEdit} className="text-xs py-1 px-3 h-auto">
                Edit
              </Button>
              {hasAnyDetail && ( 
                <Button 
                  variant="outline" 
                  onClick={() => setDetailsVisible(!detailsVisible)} 
                  icon={detailsVisible ? <EyeOff size={14}/> : <Eye size={14}/>}
                  className="text-xs py-1 px-3 h-auto"
                >
                  {detailsVisible ? "Hide" : "Show"} Details
                </Button>
              )}
            </div>
          </div>

          {hasAnyDetail ? (
            detailsVisible ? ( 
              <div className="space-y-3 mt-2 pt-2 border-t border-gray-700/30">
                {journalFields.map(field => (
                  (field.value && String(field.value).trim() !== "") ? ( 
                    <div key={field.label}>
                      <h6 className="text-xs font-semibold text-primary-300 mb-1 flex items-center">
                        {React.cloneElement(field.icon, {className: "mr-1.5 text-primary-400", size:14})} {field.label}:
                      </h6>
                      <p className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed bg-gray-800/30 p-2.5 rounded-md border border-gray-700/30 min-h-[30px]">
                        {String(field.value)}
                      </p>
                    </div>
                  ) : null
                ))}
              </div>
            ) : ( 
              <div className="mt-2 pt-2 border-t border-gray-700/30">
                <p className="text-sm text-gray-400 italic whitespace-pre-wrap">
                  {previewText} 
                </p>
              </div>
            )
          ) : ( 
            <p className="text-sm text-gray-500 italic bg-gray-800/30 p-3 rounded-md border border-gray-700/30 min-h-[40px] mt-2">
              No journal details entered. Click "Edit" to add.
            </p>
          )}
        </div>
      </div>
    </Card>
  );
};

const JournalTab = ({ tradeHistory, journalSearchTerm, setJournalSearchTerm, onSaveNote, accountInfo }) => { 
  const [editingTrade, setEditingTrade] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [visibleJournalCount, setVisibleJournalCount] = useState(INITIAL_JOURNAL_DISPLAY_COUNT);

  const handleEditClick = (trade) => {
    setEditingTrade(trade);
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setEditingTrade(null);
  };

  const sortedAndFilteredJournalEntries = useMemo(() => {
    if (!tradeHistory) return [];
    let filtered = tradeHistory;
    if (journalSearchTerm) {
      const searchTermLower = journalSearchTerm.toLowerCase();
      filtered = tradeHistory.filter(trade =>
        ((trade.symbol && trade.symbol.toLowerCase().includes(searchTermLower)) ||
        (trade.notes && trade.notes.toLowerCase().includes(searchTermLower)) ||
        (trade.strategy && trade.strategy.toLowerCase().includes(searchTermLower)) ||
        (trade.reasonEntry && trade.reasonEntry.toLowerCase().includes(searchTermLower)) ||
        (trade.reasonExit && trade.reasonExit.toLowerCase().includes(searchTermLower)) ||
        (trade.lessonsLearned && trade.lessonsLearned.toLowerCase().includes(searchTermLower)) ||
        (trade.type && trade.type.toLowerCase().includes(searchTermLower)) ||
        (String(trade.id || trade.ticket).toLowerCase().includes(searchTermLower)))
      );
    }
    
    return [...filtered].sort((a, b) => {
      const aNeedsSpecificJournaling = ![a.strategy, a.reasonEntry, a.reasonExit, a.lessonsLearned]
                                        .some(val => val && String(val).trim() !== "");
      const bNeedsSpecificJournaling = ![b.strategy, b.reasonEntry, b.reasonExit, b.lessonsLearned]
                                        .some(val => val && String(val).trim() !== "");

      if (aNeedsSpecificJournaling && !bNeedsSpecificJournaling) return -1;
      if (!aNeedsSpecificJournaling && bNeedsSpecificJournaling) return 1;

      const dateA = new Date(`${a.date}T${a.time || '00:00:00'}`);
      const dateB = new Date(`${b.date}T${b.time || '00:00:00'}`);
      return dateB - dateA;
    });
  }, [tradeHistory, journalSearchTerm]);

  const journalEntriesToShow = sortedAndFilteredJournalEntries.slice(0, visibleJournalCount);

  return (
    <div className="space-y-6">
      <Card>
        <div className="p-5">
          <div className="flex flex-col md:flex-row justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold text-gray-100 mb-3 md:mb-0 flex items-center">
              <Brain className="h-7 w-7 mr-3 text-primary-400" />Trading Journal
            </h2>
            <SearchInput 
              value={journalSearchTerm} 
              onChange={(e) => setJournalSearchTerm(e.target.value)} 
              placeholder="Search journal..." 
            />
          </div>
          {journalEntriesToShow.length > 0 ? (
            <div className="space-y-4">
              {journalEntriesToShow.map((trade) => (
                <JournalEntryCard 
                  key={trade.id || trade.ticket} 
                  trade={trade} 
                  onEdit={() => handleEditClick(trade)}
                  currency={accountInfo.currency}
                />
              ))}
            </div>
          ) : ( 
            <div className="text-center py-10">
              <MessageSquare size={48} className="mx-auto text-gray-600 mb-4" />
              <p className="text-gray-500">
                {journalSearchTerm ? 'No journal entries match your search.' : 'No trades found in history to journal.'}
              </p>
              {!journalSearchTerm && (
                <p className="text-sm text-gray-600 mt-2">
                  Your trade history will appear here. Click "Edit" on a trade to add details.
                </p>
              )}
            </div>
          )}
          {visibleJournalCount < sortedAndFilteredJournalEntries.length && (
            <div className="mt-8 text-center">
              <Button 
                variant="outline" 
                onClick={() => setVisibleJournalCount(prev => prev + JOURNAL_LOAD_MORE_COUNT)} 
                icon={<ChevronsDown />}
              >
                View More Entries ({sortedAndFilteredJournalEntries.length - visibleJournalCount} remaining)
              </Button>
            </div>
          )}
        </div>
      </Card>
      {showEditModal && editingTrade && (
        <EditNoteModal
          isOpen={showEditModal}
          onClose={handleCloseModal}
          trade={editingTrade}
          onSave={onSaveNote} 
        />
      )}
    </div>
  );
};

// --- Main Dashboard Component ---
const Dashboard = () => {
  
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [periodFilter, setPeriodFilter] = useState('30d');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  const [journalSearchTerm, setJournalSearchTerm] = useState('');
  const [tradeHistorySearchTerm, setTradeHistorySearchTerm] = useState('');

  const showNotification = (message, type = 'success') => { 
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 3500);
  };

  const handleSaveNote = async (ticket, journalDetails) => { 
    if (!SCRIPT_URL || SCRIPT_URL === "YOUR_APPS_SCRIPT_WEB_APP_URL") {
      const msg = "Error: Apps Script URL is not configured in the React app.";
      console.error(msg);
      showNotification(msg, "error");
      throw new Error(msg);
    }
    console.log(`Saving journal details for ticket: ${ticket}, Details: ${JSON.stringify(journalDetails)}`);
    try {
      const payload = { 
        password: SHEET_PASSWORD, 
        action: "update_trade_journal_details", 
        ticket: ticket, 
        details: journalDetails 
      };
      const response = await fetch(SCRIPT_URL, {
        method: "POST", 
        mode: "cors", 
        credentials: "omit", 
        redirect: "follow", 
        body: JSON.stringify(payload), 
      });

      const responseText = await response.text();
      let result;
      try { 
        result = JSON.parse(responseText); 
      } catch (parseError) {
        console.error("Failed to parse JSON response:", responseText, "| Status:", response.status);
        const errorMsg = `Error: Invalid response from server (Status: ${response.status}). Check console.`;
        showNotification(errorMsg, "error");
        throw new Error(errorMsg);
      }
      
      if (result.status === "success") {
        setData(prevData => {
          if (!prevData || !prevData.tradeHistory) return prevData;
          const updatedTradeHistory = prevData.tradeHistory.map(trade =>
            (String(trade.ticket) === String(ticket) || String(trade.id) === String(ticket)) 
            ? { ...trade, ...journalDetails } 
            : trade
          );
          return { ...prevData, tradeHistory: updatedTradeHistory };
        });
        showNotification("Journal updated successfully!", "success");
      } else {
        console.error("Error from Apps Script:", result.message);
        showNotification(`Error: ${result.message || 'Failed to save journal.'}`, "error");
        throw new Error(result.message || "Failed to save journal from script.");
      }
    } catch (err) {
      console.error("Network or other error saving journal:", err);
      const displayError = err.message.includes("Failed to fetch") 
        ? "Network error or CORS issue. Check console and Apps Script deployment." 
        : err.message;
      showNotification(`Error: ${displayError || 'Could not save journal.'}`, "error");
      throw err; 
    }
  };

  const prepareCumulativeData = (tradeHistory) => { 
    if (!tradeHistory || tradeHistory.length === 0) return [];
    let cumulativeProfit = 0;
    let peak = 0;
    const sortedHistory = [...tradeHistory].sort((a,b) => {
      const dateA = new Date(`${a.date}T${a.time || '00:00:00'}`);
      const dateB = new Date(`${b.date}T${b.time || '00:00:00'}`);
      return dateA - dateB;
    });

    return sortedHistory.map(trade => {
      cumulativeProfit += (trade.netProfit || 0); // Use netProfit for the curve now
      if (cumulativeProfit > peak) {
        peak = cumulativeProfit;
      }
      const drawdownPercent = peak > 0 ? ((peak - cumulativeProfit) / peak) * 100 : 0;
      return { ...trade, cumulativeProfit, drawdownPercent };
    });
  };

  useEffect(() => { 
    const loadData = async () => {
      setIsLoading(true); 
      setError(null);
      try {
        const sheetData = await fetchTradingDataFromSheets(periodFilter); // Pass the filter
        if (sheetData && sheetData.accountInfo && sheetData.statistics && 
            sheetData.tradeHistory && sheetData.openPositions && 
            sheetData.symbolPerformance && sheetData.timePerformance && sheetData.dailyReturns) {
          
          const institutionalMetrics = calculateInstitutionalMetrics(
            sheetData.dailyReturns,
            sheetData.tradeHistory, 
            sheetData.accountInfo,
            sheetData.statistics
          );
          
          setData({
            ...sheetData,
            institutionalMetrics
          });
        } else {
          throw new Error("Incomplete or malformed data from Google Sheets");
        }
      } catch (err) {
        console.error("Error in loadData:", err);
        setError(`Error loading data: ${err.message}. Please check your configuration.`);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [periodFilter]);

  if (isLoading) { 
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-to-br from-gray-900 to-gray-950 text-gray-200">
        <div className="relative text-center p-8 rounded-2xl bg-gray-800/30 backdrop-blur-md border border-gray-700/50 shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-tr from-primary-600/10 to-primary-400/5 rounded-2xl"></div>
          <div className="relative">
            <div className="flex justify-center items-center mb-6">
              <div className="relative">
                <div className="animate-ping absolute h-16 w-16 rounded-full bg-primary-500 opacity-20"></div>
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary-500"></div>
              </div>
            </div>
            <p className="mt-4 text-lg font-semibold bg-clip-text text-transparent bg-gradient-to-r from-primary-400 to-blue-400">
              Initializing Dashboard...
            </p>
            <p className="mt-2 text-sm text-gray-400">Synchronizing market data</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-to-br from-gray-900 to-gray-950 text-gray-200">
        <Card className="max-w-md">
          <div className="p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Error Loading Data</h2>
            <p className="text-gray-400 mb-4">{error}</p>
            <Button onClick={() => window.location.reload()}>Retry</Button>
          </div>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  const cumulativeData = prepareCumulativeData(data.tradeHistory); 
  const { accountInfo, statistics, tradeHistory, openPositions, symbolPerformance, institutionalMetrics } = data;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview': 
        return <OverviewTab 
          accountInfo={accountInfo} 
          statistics={statistics} 
          cumulativeData={cumulativeData} 
          institutionalMetrics={institutionalMetrics}
        />;
      case 'performance': 
        return <PerformanceTab data={data} />; 
      case 'symbols': 
        return <SymbolsTab symbolPerformance={symbolPerformance} accountInfo={accountInfo} />;
      case 'trades': 
        return <TradeHistoryTab 
          tradeHistory={tradeHistory} 
          accountInfo={accountInfo} 
          searchTerm={tradeHistorySearchTerm} 
          setSearchTerm={setTradeHistorySearchTerm} 
          statistics={statistics} 
          itemsPerPage={TRADE_HISTORY_ITEMS_PER_PAGE}
        />;
      case 'positions': 
        return <OpenPositionsTab openPositions={openPositions} accountInfo={accountInfo} />;
      case 'journal': 
        return <JournalTab 
          tradeHistory={tradeHistory} 
          journalSearchTerm={journalSearchTerm} 
          setJournalSearchTerm={setJournalSearchTerm} 
          onSaveNote={handleSaveNote} 
          accountInfo={accountInfo} 
        />;
      default: 
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-gray-200 font-sans antialiased">
      {/* Background effects */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwMCIgaGVpZ2h0PSIyMDAwIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxmaWx0ZXIgaWQ9Im5vaXNlIj48ZmVUdXJidWxlbmNlIHR5cGU9ImZyYWN0YWxOb2lzZSIgYmFzZUZyZXF1ZW5jeT0iMC43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgbnVtT2N0YXZlcz0iMiIgc2VlZD0iMiIgcmVzdWx0PSJ0dXJidWxlbmNlIj48L2ZlVHVyYnVsZW5jZT48ZmVDb2xvck1hdHJpeCB0eXBlPSJzYXR1cmF0ZSIgdmFsdWVzPSIwIiByZXN1bHQ9ImRlc2F0dXJhdGVkVHVyYnVsZW5jZSI+PC9mZUNvbG9yTWF0cml4PjxmZUJsZW5kIGluPSJTb3VyY2VHcmFwaGljIiBpbjI9ImRlc2F0dXJhdGVkVHVyYnVsZW5jZSIgbW9kZT0ib3ZlcmxheSIgcmVzdWx0PSJibGVuZGVkVHVyYnVsZW5jZSI+PC9mZUJsZW5kPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbHRlcj0idXJsKCNub2lzZSkiIG9wYWNpdHk9IjAuMDUiPjwvcmVjdD48L3N2Zz4=')] opacity-40 pointer-events-none z-0 mix-blend-soft-light"></div>
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(110,86,207,0.15),transparent_25%)]"></div>
        {[...Array(4)].map((_, i) => (
          <>
            <div key={`h-${i}`} className={`absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-500/${i===0||i===3?20:10} to-transparent`} style={{top: `${i * 33.33}%`}}></div>
            <div key={`v-${i}`} className={`absolute top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-primary-500/${i===0||i===3?20:10} to-transparent`} style={{left: `${i * 33.33}%`}}></div>
          </>
        ))}
      </div>
      
      <div className="relative z-10">
        <header className="bg-gray-900/90 backdrop-blur-md shadow-lg border-b border-gray-800 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 justify-between items-center">
              <div className="flex items-center space-x-3">
                <Hexagon className="h-8 w-8 text-primary-500" />
                <h1 className="ml-2 text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-blue-400">
                  Trading Dashboard
                </h1>
              </div>
              <div className="flex items-center space-x-5">
                <div className="hidden md:flex items-center space-x-2 border border-gray-700 rounded-lg p-2 bg-gray-800/50 backdrop-blur-sm">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <select 
                    className="bg-transparent text-gray-300 focus:outline-none text-sm appearance-none pr-6 cursor-pointer" 
                    value={periodFilter} 
                    onChange={(e) => setPeriodFilter(e.target.value)} 
                    style={{ 
                      backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`, 
                      backgroundRepeat: 'no-repeat', 
                      backgroundPosition: 'right center', 
                      backgroundSize: '1em' 
                    }}
                  >
                    <option value="7d">Last 7 days</option>
                    <option value="30d">Last 30 days</option>
                    <option value="90d">Last 90 days</option>
                    <option value="ytd">Year to date</option>
                    <option value="all">All time</option>
                  </select>
                </div>
                <div className="relative">
                  <div className="px-4 py-2 flex items-center rounded-lg bg-gradient-to-r from-primary-800/80 to-primary-600/80 text-white shadow-lg">
                    <DollarSign className="h-4 w-4 mr-2 text-primary-300" />
                    <span className="font-medium">{accountInfo.currency} {accountInfo.balance?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 h-px w-3/4 bg-gradient-to-r from-transparent via-primary-400 to-transparent"></div>
                </div>
                <button className="p-1 rounded-full text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-primary-500">
                  <UserCircle className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>
        </header>
        
        <div className="sticky top-16 bg-gray-900/80 backdrop-blur-md z-30 shadow-md border-b border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex space-x-1 md:space-x-2 overflow-x-auto hide-scrollbar">
              <TabButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
                <Activity className="h-4 w-4 mr-2" />Overview
              </TabButton>
              <TabButton active={activeTab === 'performance'} onClick={() => setActiveTab('performance')}>
                <BarChart2 className="h-4 w-4 mr-2" />Performance
              </TabButton>
              <TabButton active={activeTab === 'symbols'} onClick={() => setActiveTab('symbols')}>
                <Package className="h-4 w-4 mr-2" />Symbols
              </TabButton>
              <TabButton active={activeTab === 'trades'} onClick={() => setActiveTab('trades')}>
                <BookOpen className="h-4 w-4 mr-2" />History
              </TabButton>
              <TabButton active={activeTab === 'positions'} onClick={() => setActiveTab('positions')}>
                <Layers className="h-4 w-4 mr-2" />Positions
              </TabButton>
              <TabButton active={activeTab === 'journal'} onClick={() => setActiveTab('journal')}>
                <Brain className="h-4 w-4 mr-2" />Journal
              </TabButton>
            </nav>
          </div>
        </div>
        
        {/* Notification Area */}
        {notification.show && (
          <div className={`fixed top-20 right-5 z-[150] p-4 rounded-lg shadow-xl text-sm text-white flex items-center transition-all duration-300 ease-in-out ${
            notification.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          }`} style={{
            transform: notification.show ? 'translateX(0)' : 'translateX(100%)', 
            opacity: notification.show ? 1: 0
          }}>
            {notification.type === 'success' ? <ThumbsUp size={18} className="mr-2"/> : <AlertCircle size={18} className="mr-2"/>}
            {notification.message}
          </div>
        )}
        
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {error && (
            <div className="mb-6">
              <div className="rounded-lg bg-gray-800/70 backdrop-blur-sm border border-orange-600/30 px-4 py-3 flex items-center shadow-lg">
                <AlertTriangle className="h-5 w-5 text-orange-500 mr-3" />
                <span className="text-orange-200 text-sm">{error}</span>
              </div>
            </div>
          )}
          {renderTabContent()}
        </main>
        
        
      </div>
    </div>
  );
};

export default Dashboard;
